import { useMemo } from "react";
import { useLocation } from "react-router";
import { useMe } from "./useMe.js";
import { useSystemApps, type QuickNavItem } from "./useSystemApps.js";
import { useSystemMeta } from "./useSystemMeta.js";
import { useUserPreferences } from "./useUserPreferences.js";
import { useAmcCapabilities, type AmcCapabilities, type AmcTab } from "./useAmcCapabilities.js";
import {
  navSections,
  softwareCategoryMeta,
  type NavLinkItem,
  type NavCapabilityGate,
  type NavSection as NavSectionConfig,
  type NavGroup
} from "../nav/navConfig.js";
import { isAdminUser, isBannedUser, hasPermission } from "../lib/permissions.js";
import {
  DEFAULT_PROFILE_TAB,
  PROFILE_PATH,
  getProfileTabFromHref,
  getProfileTabHref,
  isProfileRoute,
  isProfileTabActive,
  type ProfileTabId
} from "../lib/profileTabs.js";
import { applyCategoryOrder } from "../lib/nav/applyCategoryOrder.js";
import { notificationEventGroups, isAdminOnlyEventKey } from "../lib/notifications/preferences.js";

export type VisibleSection = NavSectionConfig & { items: NavLinkItem[]; groups?: NavGroup[] };

const extractProfileTabFromItem = (itemPath: string): ProfileTabId | null => {
  if (!itemPath.startsWith(PROFILE_PATH)) return null;
  return getProfileTabFromHref(itemPath) ?? DEFAULT_PROFILE_TAB;
};

/**
 * Pathnames that have at least one query-param nav item (e.g. `/content/media?tab=brand`).
 * A no-query sibling at the same pathname (e.g. Asset Management Center's "Local Assets" at
 * `/content/media`) must NOT highlight when the URL carries a query that belongs to one
 * of those siblings — otherwise both the no-query item and the active sub-tab light up.
 * Plain-path pages with internal `?tab=` state but no query-param NAV siblings (e.g.
 * `/settings/email`, `/settings/notifications`) are intentionally absent here, so they
 * stay active across their own query params.
 */
const PATHNAMES_WITH_QUERY_SIBLINGS: ReadonlySet<string> = (() => {
  const result = new Set<string>();
  const walk = (items?: NavLinkItem[]) => {
    for (const item of items ?? []) {
      const qIdx = item.path.indexOf("?");
      if (qIdx !== -1) result.add(item.path.slice(0, qIdx));
    }
  };
  const walkGroups = (groups?: NavGroup[]) => {
    for (const group of groups ?? []) {
      walk(group.items);
      walkGroups(group.groups);
    }
  };
  for (const section of navSections) {
    walk(section.items);
    walkGroups(section.groups);
  }
  return result;
})();

export const useVisibleSections = () => {
  const { user, appConfig } = useMe();
  const isAdmin = isAdminUser(user);
  const isStreamingEnabled = appConfig?.features?.wsdashboardEnabled ?? false;
  const isWebconsoleEnabled = appConfig?.features?.webconsoleEnabled ?? false;
  const isTransferEnabled = appConfig?.features?.interInstanceTransferEnabled ?? false;
  const isCommandChannelEnabled = appConfig?.notifications?.commandChannelEnabled ?? false;
  const { installedApps: systemInstalledApps, quickNavItems, hasMediaServer } = useSystemApps();
  const { capabilities: amcCapabilities } = useAmcCapabilities();
  const systemMeta = useSystemMeta({ refreshMs: 120_000 });
  const { preferences: userPrefs, update: updatePrefs, saving: prefsSaving } = useUserPreferences();
  const location = useLocation();
  const isBanned = isBannedUser(user);

  const hasUpdatesAvailable = useMemo(() => {
    const dashboardUpdate = systemMeta.data?.dashboardVersion?.updateAvailable ?? false;
    const qbUpdate = systemMeta.data?.updateAvailable ?? false;
    return dashboardUpdate || qbUpdate;
  }, [systemMeta.data?.dashboardVersion?.updateAvailable, systemMeta.data?.updateAvailable]);

  const fallbackSection = useMemo<VisibleSection>(() => {
    const groups: NavGroup[] = [
      {
        id: "account-profile",
        label: "Profile",
        icon: "lucide:user",
        items: [
          { id: "profile-picture", label: "Profile picture", path: `${getProfileTabHref("profile")}#profile-picture`, icon: "lucide:image" },
          { id: "profile-account", label: "Account", path: `${getProfileTabHref("profile")}#profile-account`, icon: "lucide:settings" },
          { id: "profile-links", label: "Quick links", path: `${getProfileTabHref("profile")}#profile-links`, icon: "lucide:link" },
          { id: "profile-system-metrics", label: "System metrics", path: `${getProfileTabHref("profile")}#profile-system-metrics`, icon: "lucide:activity" }
        ]
      },
      {
        id: "account-security",
        label: "Security",
        icon: "lucide:shield",
        items: [
          { id: "security-passkeys", label: "Passkeys", path: `${getProfileTabHref("security")}#security-passkeys`, icon: "lucide:key" },
          { id: "security-2fa", label: "Two-factor auth", path: `${getProfileTabHref("security")}#security-2fa`, icon: "lucide:smartphone" },
          { id: "security-sessions", label: "Sessions", path: `/profile?tab=security&view=sessions`, icon: "lucide:monitor" }
        ]
      },
      {
        id: "account-notification-settings",
        label: "Notification Settings",
        icon: "lucide:bell",
        items: [
          { id: "notification-settings-channels", label: "Channels", path: `${getProfileTabHref("notification-settings")}#notification-settings-channels`, icon: "lucide:radio" },
          { id: "notification-settings-channel-setup", label: "Channel setup", path: `${getProfileTabHref("notification-settings")}#notification-settings-channel-setup`, icon: "lucide:wrench" },
          { id: "notification-settings-disk-usage", label: "Disk usage alerts", path: `${getProfileTabHref("notification-settings")}#notification-settings-disk-usage`, icon: "lucide:hard-drive" },
          { id: "notification-settings-updates-digest", label: "Updates digest", path: `${getProfileTabHref("notification-settings")}#notification-settings-updates-digest`, icon: "lucide:calendar-clock" },
          { id: "notification-settings-events", label: "Notification events", path: `${getProfileTabHref("notification-settings")}#notification-settings-events`, icon: "lucide:bell-ring" },
          // Admin-gating parity with `NotificationsPanel`: drop any group whose
          // events are entirely admin-only when the viewer is not an admin.
          // Without this, non-admins get a sidebar link pointing to a section
          // that isn't rendered (broken jump + leaked group title).
          ...notificationEventGroups
            .filter((group) => isAdmin || group.events.some((event) => !isAdminOnlyEventKey(event.key)))
            .map((group) => ({
              id: `notification-settings-events-${group.id}`,
              label: group.title,
              path: `${getProfileTabHref("notification-settings")}#notification-settings-events-${group.id}`,
              icon: ""
            }))
        ]
      },
      {
        id: "account-notification-center",
        label: "Notification Center",
        icon: "lucide:inbox",
        items: [
          { id: "notification-center-all", label: "All notifications", path: getProfileTabHref("notification-center"), icon: "lucide:inbox" }
        ]
      },
    ];

    // Command channel sub-tab — only when enabled install-wide (OFF by default).
    if (isCommandChannelEnabled) {
      groups.push({
        id: "account-command-channel",
        label: "Command Channel",
        icon: "lucide:terminal",
        items: [
          { id: "command-channel-consent", label: "Enable & consent", path: `${getProfileTabHref("command-channel")}#command-channel-consent`, icon: "lucide:toggle-right" },
          { id: "command-channel-links", label: "Linked channels", path: `${getProfileTabHref("command-channel")}#command-channel-links`, icon: "lucide:link" },
          { id: "command-channel-aliases", label: "Command aliases", path: `${getProfileTabHref("command-channel")}#command-channel-aliases`, icon: "lucide:wand-2" }
        ]
      });
    }

    if (hasPermission(user, "admin.system.support")) {
      groups.push({
        id: "account-support",
        label: "Support",
        icon: "lucide:life-buoy",
        items: [
          { id: "support-access", label: "Enable support access", path: getProfileTabHref("support"), icon: "lucide:key-round", requiresPermission: "admin.system.support" }
        ]
      });
    }

    const items = groups.flatMap((g) => g.items);

    return {
      id: "account",
      label: "Account",
      icon: "lucide:user-circle",
      items,
      groups
    };
  }, [isAdmin, user, isCommandChannelEnabled]);

  const isFeatureVisible = (feature?: string) => {
    if (!feature) return true;
    if (feature === "wsdashboard") return isStreamingEnabled && hasMediaServer;
    if (feature === "webconsole") return isWebconsoleEnabled;
    if (feature === "inter-instance-transfer") return isTransferEnabled;
    // Access is granted, never inferred from what happens to be installed:
    // owning a Seerr or Ombi install says nothing about whether someone may
    // review the queue. `hasPermission` already resolves the server admin, so
    // this reads as "was this granted?" and nothing else.
    if (feature === "media-requests") {
      return hasPermission(user, "admin.requests.read");
    }
    return true;
  };

  // Async capability gates (AMC). Until the capability contract loads, gated
  // entries stay hidden — admins still see the entry the moment it resolves
  // (`canAccess:true`), so there's no persistent loss; only a brief absence.
  const passesCapability = (gate: NavCapabilityGate | undefined, caps: AmcCapabilities | null) => {
    if (!gate) return true;
    if (!caps?.canAccess) return false;
    if (gate === "amc-access") return true;
    const tab = gate.slice("amc-tab:".length) as AmcTab;
    return caps.allowedTabs.includes(tab);
  };

  const dynamicSoftwareGroups = useMemo<NavGroup[] | undefined>(() => {
    if (quickNavItems.length === 0) return undefined;

    const byCategory = new Map<string, QuickNavItem[]>();
    for (const item of quickNavItems) {
      const list = byCategory.get(item.category);
      if (list) {
        list.push(item);
      } else {
        byCategory.set(item.category, [item]);
      }
    }

    const knownOrder = Object.keys(softwareCategoryMeta);
    const defaultCategories = [
      ...knownOrder.filter((cat) => byCategory.has(cat)),
      ...[...byCategory.keys()].filter((cat) => !softwareCategoryMeta[cat])
    ];

    const sortedCategories = applyCategoryOrder(
      defaultCategories,
      userPrefs.softwareQuickNavCategoryOrder
    );

    return sortedCategories.map((category): NavGroup => {
      const items = byCategory.get(category) ?? [];
      const meta = softwareCategoryMeta[category];

      const navItems: NavLinkItem[] = items.map((qi) => ({
        id: qi.id,
        label: qi.label,
        path: qi.href,
        icon: qi.icon,
        external: qi.hrefType === "external"
      }));

      const fallbackLabel = category
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

      return {
        id: category,
        label: meta?.label ?? fallbackLabel,
        icon: meta?.icon ?? "lucide:folder",
        items: navItems
      };
    });
  }, [quickNavItems, userPrefs.softwareQuickNavCategoryOrder]);

  const visibleSections = useMemo<VisibleSection[]>(() => {
    if (isBanned) return [];

    const filterItem = (item: NavLinkItem) => {
      if (item.requiresRole === "admin" && !isAdmin) return false;
      if (!isFeatureVisible(item.requiresFeature)) return false;
      if (item.requiresPermission && !hasPermission(user, item.requiresPermission)) return false;
      if (item.requiresApp && !systemInstalledApps.has(item.requiresApp)) return false;
      if (!passesCapability(item.requiresCapability, amcCapabilities)) return false;
      return true;
    };

    return navSections
      .map((section): VisibleSection | null => {
        if (section.id === "software" && dynamicSoftwareGroups) {
          const flatItems = dynamicSoftwareGroups.flatMap((g) => g.items);
          return {
            ...section,
            items: flatItems,
            groups: dynamicSoftwareGroups.length > 0 ? dynamicSoftwareGroups : undefined
          };
        }

        const visibleItems = (section.items ?? []).filter(filterItem);

        const filterGroup = (group: NavGroup): NavGroup | null => {
          // Children decide whether the group appears.
          //
          // A group's own gate used to be evaluated first and hide everything
          // beneath it, which made it impossible to place an individually
          // grantable page inside a group whose other children are admin-only:
          // the grant worked, but the entry was invisible. Filtering children
          // first and keeping the group when any survive lets a grantee see
          // exactly the one entry they were given, in its natural home.
          //
          // This does not widen anything on its own. Every child carries its
          // own gate and is filtered by `filterItem` regardless, so an
          // admin-only sibling stays hidden; a group whose children are all
          // gated the same way as the group still disappears whole, because
          // none of them survive.
          const groupItems = group.items.filter(filterItem);
          const childGroups = group.groups
            ?.map(filterGroup)
            .filter((g): g is NavGroup => g !== null);
          if (!groupItems.length && (!childGroups || !childGroups.length)) return null;

          // The group's own gate still decides whether its HEADER is a link.
          // Without this, a grantee would get a header pointing at the group's
          // landing page — which they may not open — and clicking the thing
          // that names their feature would answer "forbidden".
          const mayOpenGroupLanding =
            !(group.requiresRole === "admin" && !isAdmin) &&
            isFeatureVisible(group.requiresFeature) &&
            !(group.requiresPermission && !hasPermission(user, group.requiresPermission)) &&
            passesCapability(group.requiresCapability, amcCapabilities);

          const { path: groupPath, ...rest } = group;
          return {
            ...rest,
            ...(mayOpenGroupLanding && groupPath ? { path: groupPath } : {}),
            items: groupItems,
            groups: childGroups?.length ? childGroups : undefined
          };
        };

        const visibleGroups = section.groups
          ?.map(filterGroup)
          .filter((group): group is NavGroup => Boolean(group));

        const flattenGroupItems = (groups: NavGroup[]): NavLinkItem[] =>
          groups.flatMap((g) => [...g.items, ...(g.groups ? flattenGroupItems(g.groups) : [])]);

        const flattenedItems = [
          ...visibleItems,
          ...(visibleGroups ? flattenGroupItems(visibleGroups) : [])
        ];

        if (!flattenedItems.length) return null;

        return { ...section, items: flattenedItems, groups: visibleGroups ?? undefined };
      })
      .filter((section): section is VisibleSection => section !== null);
  }, [isBanned, isAdmin, isStreamingEnabled, isWebconsoleEnabled, isTransferEnabled, hasMediaServer, user, systemInstalledApps, dynamicSoftwareGroups, amcCapabilities]);

  const installedSoftwareIds = useMemo(() => {
    const softwareSection = visibleSections.find((section) => section.id === "software");
    if (!softwareSection) return new Set<string>();

    const items = softwareSection.groups?.flatMap((group) => group.items) ?? softwareSection.items;

    const isDynamicSection = dynamicSoftwareGroups != null && dynamicSoftwareGroups.length > 0;

    const installed = items.filter((item) => {
      if (item.disabled) return false;
      if (!isDynamicSection && item.external) return false;
      return true;
    });

    return new Set(installed.map((item) => item.id));
  }, [visibleSections, dynamicSoftwareGroups]);

  const isNavItemActive = (item: NavLinkItem, pathname: string, search: string) => {
    if (item.disabled) return false;
    if (item.external) return false;

    if (isProfileRoute(pathname) && item.path.startsWith(PROFILE_PATH)) {
      const tab = extractProfileTabFromItem(item.path);
      if (!isProfileTabActive(pathname, search, tab ?? DEFAULT_PROFILE_TAB)) return false;

      const itemUrl = new URL(item.path, "http://local");
      const itemView = itemUrl.searchParams.get("view");
      if (itemView) {
        const currentView = new URLSearchParams(search).get("view");
        return itemView === currentView;
      }

      const hashIndex = item.path.indexOf("#");
      if (hashIndex !== -1) {
        if (location.hash) {
          return location.hash === item.path.slice(hashIndex);
        }
        return false;
      }
      return true;
    }

    // Handle items with query parameters (e.g., /streaming/dashboard/control-center?domain=status-health)
    const qIdx = item.path.indexOf("?");
    if (qIdx !== -1) {
      const itemPathname = item.path.slice(0, qIdx);
      const itemSearch = item.path.slice(qIdx);
      return pathname === itemPathname && search === itemSearch;
    }

    // Handle items with hash fragments (e.g., /settings/general#instance-settings)
    const hIdx = item.path.indexOf("#");
    if (hIdx !== -1) {
      const itemPathname = item.path.slice(0, hIdx);
      return pathname === itemPathname;
    }

    if (pathname === item.path) {
      // A no-query item whose pathname also hosts query-param siblings (e.g. Media
      // Library "Local Assets" at /content/media vs ?tab=brand|app-logos|avatars) must
      // only match when the URL itself carries no query — otherwise it co-highlights
      // with the active sub-tab. Pages with internal ?tab= state but no query-param nav
      // siblings are not in the set, so they keep matching across their own query params.
      if (search && PATHNAMES_WITH_QUERY_SIBLINGS.has(item.path)) return false;
      return true;
    }
    // Deeper routes light an entry only when it asks for that. Matching any
    // path that merely starts with this one made every landing page claim its
    // whole subtree — a Control Center "Overview" entry reads as active on
    // Devices, Media Portal and Requests alike, because its own path is a
    // prefix of all of them. Entries with genuine sub-pages (a list and its
    // detail view) opt in with `matchSubRoutes`.
    return item.matchSubRoutes === true && pathname.startsWith(`${item.path}/`);
  };

  const isNavItemTabActive = (item: NavLinkItem, pathname: string, search: string) => {
    if (item.disabled || item.external) return false;
    if (!isProfileRoute(pathname) || !item.path.startsWith(PROFILE_PATH)) return false;
    const tab = extractProfileTabFromItem(item.path);
    return isProfileTabActive(pathname, search, tab ?? DEFAULT_PROFILE_TAB);
  };

  return {
    visibleSections,
    fallbackSection,
    isAdmin,
    amcCapabilities,
    isBanned,
    isNavItemActive,
    isNavItemTabActive,
    hasUpdatesAvailable,
    installedSoftwareIds,
    dynamicSoftwareGroups,
    quickNavItems,
    userPrefs,
    updatePrefs,
    prefsSaving
  };
};
